const cardsArray = [
  {
    tag: "Introduction",
    header: "What is Fixel?",
    id: "What is Fixel".replaceAll(" ", "_"),
    snippet: [
      <p>
        Fixel is a fully automated segmentation tool that ranks your website visitors based on their
        level of engagement.
      </p>,
      <p>
        Our platform learns how users interact with your site, and by applying advanced machine
        learning algorithms, we highlight those that matter most.
      </p>,
    ],
  },
  {
    tag: "Introduction",
    header: "How does Fixel work?",
    id: "How does Fixel work".replaceAll(" ", "_"),
    snippet: [
      <p>
        Once installed on the site, the Fixel snippet tracks dozens of users attributes and
        interactions as they go through your website.
      </p>,
      <p> The script works automatically out of the box, no need for any granular setup.</p>,
      <p>
        The data collected is processed by our Machine Learning engine which builds a unique
        engagement model for every site it is installed on.
      </p>,
      <p>
        Once the model is ready, an additional layer of Predictive Analytics matches users as they
        come in with their predicted score.
      </p>,
      <p>
        Every user going through the website receives a score based on his level of engagement. With
        every engagement threshold passed, the user’s score is incremented accordingly.
      </p>,
      <p>
        The user scoring is passed in real-time into your ad platforms - Google and Facebook Ads.
        You can immediately act on this data in whatever way you see fit.
      </p>,
    ],
  },
  {
    tag: "Introduction",
    header: "Why doesn't Fixel run immediately?",
    id: "Why doesn't Fixel run immediately".replaceAll(" ", "_"),
    snippet: [
      <p>Your site is unique.</p>,
      <p>
        The Fixel algorithm builds a machine learning model that learns each site’s individual
        behavior. This way it understands exactly how users are engaging with your site, and not
        some arbitrary industry benchmarks that might not fit your needs.
      </p>,
      <p>
        This learning process by the machine learning algorithm is called “training” and requires
        500 sessions. Usually, after 24 hours you can expect the model to be fully trained and see
        the Fixel events triggered in your ad platforms.
      </p>,
      <p>
        If after 7 days you still can’t see any events, please contact us and we’ll look into it
        with you.
      </p>,
    ],
  },
  {
    tag: "Facebook Ads",
    header: "Using Fixel's Advanced Facebook Ads integration",
    id: "Using Fixel's Advanced Facebook Ads integration".replaceAll(" ", "_"),
    snippet: [
      <p>
        Fixel offers advertisers the option to automatically connect with their ad account to create
        high-value audiences. Creating these audiences can also be done manually {"("}see below{")"}
        , but this integration creates our battle-tested audiences from a combination of different
        lookback windows and session quality.
      </p>,
      <p>The audiences created are:</p>,
      <ol>
        <li>
          Fixel Short-term Engaged Audience - Ideal for <b>focused remarketing</b> to recent site
          visitors {"("}up to 30 days{")"}.
        </li>
        <li>
          Fixel Long-term Engaged Audience - Ideal for <b>broad remarketing</b> to site visitors{" "}
          {"("}up to 180 days{")"}
        </li>
        <li>
          Fixel Lookalike Seed - Ideal for creating <b>Lookalike audiences</b> from high-value site
          visitors
        </li>
      </ol>,
    ],
  },
  {
    tag: "Facebook Ads",
    header: "Manually creating Fixel audiences in Facebook Ads",
    id: "Creating Fixel audiences in Facebook Ads".replaceAll(" ", "_"),
    snippet: [
      <p>To create an audience for use in Facebook please follow these steps:</p>,
      <ol>
        <li>Go to the Audiences tab in your Facebook ads account/business manager.</li>
        <li>Click on Create audience {"->"} Custom audience.</li>
        <li>Choose Website traffic.</li>
        <li>
          Choose the High level in the event list {"("}it will appear with your segment name{")"}.
        </li>
        <li>
          Do the same for "Basic" & "Med" according to your business needs {"("}list membership
          duration etc.{")"}.
        </li>
      </ol>,
      <p>
        Now that you’ve set up these audience lists you will be able to see the number of users in
        each list and once they reach a critical mass you can start running smarter campaigns on
        them.
      </p>,
    ],
  },
  {
    tag: "Facebook Ads",
    header: "Using the Fixel audiences in Facebook Ads",
    id: "Using the Fixel audiences in Facebook Ads".replaceAll(" ", "_"),
    snippet: [
      <p>The Fixel audiences can generally be used for three main purposes in Facebook Ads:</p>,
      <ol>
        <li>
          Remarketing - The visitors marked by Fixel can be targeted by the specific level of
          engagement they expressed. For example, you can create a "Fixel High 30 Days" audience to
          use in your Dynamic Remarketing campaigns {"("}
          "View content or add to cart but not purchased"{")"}. You can also use other Fixel
          engagement levels with shorter lookback windows or top of the funnel creative. .
        </li>
        <li>
          Lookalikes - Fixel's highly engaged visitors can serve as a great extension to your
          lookalike seeds after exhausting existing seeds - High Values, Purchases and Add to Cart.
          You can also cross highly engaged visitors with a specific site category visited to create
          an even more focused seed - simply select narrow by URL when creating the audience. .
        </li>
        <li>
          Creative optimization - Often times, when optimizing multiple creatives in a single ad
          set, you rely either on clicks, which are meaningless, or Add to Cart/Purchase events,
          which are too few to optimize for. By setting Fixel highly engaged as a Custom Conversion,
          you can quickly optimize your creatives at scale without sacrificing performance. .
        </li>
      </ol>,
    ],
  },
  {
    tag: "Facebook Ads",
    header: "I can't see the Fixel events in Facebook Ads",
    id: "I can't see the Fixel events in Facebook Ads".replaceAll(" ", "_"),
    snippet: [
      <p>
        If you're not seeing the Fixel events in the Audiences tab in your ad account, please follow
        the steps below:
      </p>,
      <ol>
        <li>
          Make sure your Fixel segment is Active. If your segment isn't active yet, you will see a
          counter at the top of the Fixel's app dashboard. The segment should activate automatically
          after at least 24 hours have passed and 500 sessions have been analyzed. If after 7 days
          you still can’t see any events, please contact us and we’ll look into it with you. .
        </li>
        <li>
          If the segment is Active, please check your Facebook Pixel to verify the Fixel events are
          coming in. You can check it on{" "}
          <a
              href="https://business.facebook.com/events_manager/pixel/events"
              target="_blank"
              rel="noreferrer">
            this link
          </a>
          . Make sure you select the relevant pixel and the Fixel events should appear in the table.
          The events will be formatted "Fixel_sitename_High" {"("}similarly for Basic and Med{")"}.
          If you don't see events, despite the segment being Active, please contact us. .
        </li>
      </ol>,
    ],
  },
  {
    tag: "Google Ads",
    header: "Creating Fixel audiences in Google Ads",
    id: "Creating Fixel audiences in Google Ads".replaceAll(" ", "_"),
    snippet: [
      <p>
        The Fixel audiences are sent to Google Analytics {"("}as events{")"} and can then be passed
        into Google Ads.
      </p>,
      <p>
        To import the Fixel audiences to your Google Analytics account, simply follow these steps:
      </p>,
      <ol>
        <li>
          {" "}
          Click{" "}
          <a
              href="https://analytics.google.com/analytics/web/template?uid=VzT9b6mUSQGVw45jrKaQqw"
              target="_blank"
              rel="noreferrer">
            this link
          </a>{" "}
          Import Fixel audience template to Google Analytics .
        </li>
        <li>
          {" "}
          Select the relevant View and Google Ads account you want the audiences to sync with.
        </li>
        <li> Click Create.</li>
      </ol>,
      <p>
        Alternatively, you can also create these audiences manually in Google Analytics by following
        these steps:
      </p>,
      <ol>
        <li>Log into your Google Analytics account.</li>
        <li>Navigate to the relevant Account and Property.</li>
        <li>Go to the Admin section.</li>
        <li>On the Property level, open the ‘Audience Definitions – Audiences’ section.</li>
        <li>Click the ‘+New Audience’ button.</li>
        <li>Verify that the selected View is correct under ‘Audience source’.</li>
        <li>Under ‘Audience definition’ click on ‘Create new’.</li>
        <li> Choose ‘Advanced – Conditions’.</li>
        <li>Select ‘Event Action’.</li>
        <li> Select ‘Exactly matches’.</li>
        <li>
          Enter ‘High’ {"("}use exact case{")"}.
        </li>
        <li>Click ‘Apply’.</li>
        <li>Name your audience with a memorable name, e.g ‘Fixel highly engaged users’.</li>
      </ol>,
      <p>
        Be sure to repeat this workflow for each segment of Fixel users {"("}Basic, Med, High{")"}
      </p>,
      <p>
        Now that you’ve set up these audience lists you will be able to see the number of users in
        each list and once they reach a critical mass you can start running smarter campaigns on
        them. To use these audiences in Google Ads, first make sure you have linked your Google Ads
        and Google Analytics accounts{" "}
        <a
            href="https://docs.fixel.ai/docs/using-fixel/google-ads/creating-fixel-audiences-in-google-ads/"
            target="_blank"
            rel="noreferrer">
          {"("}see how{")"}
        </a>
        .
      </p>,
    ],
  },
  {
    tag: "Google Ads",
    header: "Using the Fixel audiences in Google Ads",
    id: "Using the Fixel audiences in Google Ads".replaceAll(" ", "_"),
    snippet: [
      <p>The Fixel audiences can generally be used for three main purposes in Google Ads:</p>,
      <ol>
        <li>
          Remarketing - The visitors marked by Fixel can be targeted by the specific level of
          engagement they expressed. For example, you can create a "Fixel High 30 Days" audience to
          use in your Remarketing campaigns on GDN. You can also use lower Fixel engagement levels
          for bid adjustments on RLSA campaigns.
        </li>
        <li>
          Similar Audiences - Fixel's highly engaged visitors can serve as a great extension to your
          Similar Audiences seeds after exhausting existing seeds - High Values, Purchases and Add
          to Cart. By default, any Fixel audience created in your account will also generate a
          Similar Audience. .
        </li>
        <li>
          Scaling optimization - When optimizing multiple dimensions in a campaign, you rely either
          on clicks, which are meaningless, or conversion events, which are too few to optimize for.
          This is common when drilling into the Search Term report or a GDN placement report.
        </li>
      </ol>,
      <p>
        By setting Fixel highly engaged as a Conversion {"("}without including in "All conversions"!
        {")"}, you can quickly optimize your creatives at scale without sacrificing performance.
      </p>,
    ],
  },
  {
    tag: "Microsoft Ads",
    header: "Using the Fixel audiences in Microsoft Ads",
    id: "Using the Fixel audiences in Microsoft Ads".replaceAll(" ", "_"),
    snippet: [
      <p>
        To create an audience for use in Microsoft Ads {"("}formerly Bing Ads{")"} please follow
        these steps:
      </p>,
      <ol>
        <li>In the left pane of Microsoft Advertising, click Shared Library {">"} Audiences.</li>
        <li>
          Click <b>Create remarketing</b> list.
        </li>
        <li>
          For <b>Whom to add to your audience</b>, select <b>Custom events</b>.
        </li>
        <li>
          Choose the following parameters:
          <ol>
            <li>Category: ‘Fixel’.</li>
            <li>
              Action: ‘High’ {"("}use exact case{")"}.
            </li>
          </ol>
        </li>
        <li>
          Set the Membership duration to tell Microsoft Advertising how far back in time to look for
          actions that match your remarketing list definition in order to add people to your list.
        </li>
        <li>For Tag name, select the UET tag that you added to your website.</li>
        <li>Click Save.</li>
      </ol>,
      <p>
        Be sure to repeat this workflow for each segment of Fixel users {"("}Basic, Med, High{")"}.
      </p>,
    ],
  },
  {
    tag: "Pinterest Ads",
    header: "Using the Fixel audiences in Pinterest Ads",
    id: "Using the Fixel audiences in Pinterest Ads".replaceAll(" ", "_"),
    snippet: [
      <ol>
        <li>Log into your Pinterest business account.</li>
        <li>Click Ads at the top of the screen, then click Audiences.</li>
        <li>Click Create audience.</li>
        <li>Select Visitors who went to your site, then click Next.</li>
        <li>Enter a Name and Description for your audience.</li>
        <li>Below Pinterest tags, select your Pinterest tag.</li>
        <li>
          Enter the duration of days you want to track visitors from and optionally select to
          Include past traffic.
        </li>
        <li>
          Click Add a filter where <b>Event</b> equals <b>Fixel_High</b>.
        </li>
        <li>Click Create.</li>
      </ol>,
      <p>
        Be sure to repeat this workflow for each segment of Fixel users {"("}Basic, Med, High{")"}
      </p>,
    ],
  },
];

export default cardsArray;